// ./src/components/generic/form/OptionPickerModal.tsx
//
// A full-screen, searchable picker for phones. Mantine's dropdown is cramped and
// hard to tap on mobile when a list has more than a few options (timezones,
// currencies, categories, contacts, …), so `SelectField` — and the bespoke entity
// pickers (venue, contact, highlights, category) — swap to this on small screens:
// a sticky search + big, well-spaced tap rows. Desktop keeps the normal dropdown.
//
// Three modes on top of the plain single-select:
//   • `multiple` — rows toggle, the modal stays open, a Done button closes it.
//   • `onSearchChange` — the caller owns the search (server-side lookup); the
//     modal then does NOT filter locally.
//   • `onCreate` — a pinned "＋ Create …" row that hands the typed text back, so a
//     missing option can be created without leaving the picker.
//
// Accepts the same `data` shapes as Mantine's Select (string[],
// {value,label,disabled}[], or grouped {group, items}[]) and normalizes them.
//
// ⚠ It is THE phone dropdown (owner, 2026-10-09: "on mobile we use full screen selectors consistently"): an inline
//   Mantine dropdown inside a bottom-sheet drawer resized the sheet as it opened and as the keyboard came up — the
//   channel picker flickered and the block editor's font picker could not be used. `SelectField`, `MultiSelectField`
//   and `AutocompleteField` open this on every phone, whatever the list's length; `check-mobile-pickers` keeps raw
//   Mantine dropdowns out of the apps.

import React, { createContext, useContext, useMemo, useRef, useState, useEffect } from "react";
import { TextInput, Text, Stack, Group, Box, Button, Loader, ScrollArea, UnstyledButton, CloseButton } from "@mantine/core";
import { StackedModal } from "../StackedOverlay";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

interface PickerOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Optional entity icon (AppIcon name) + color — rendered on the row, so the
      picker looks like the entity's list page (categories, highlights…). */
  icon?: string | null;
  color?: string | null;
  /** The caller's own data item — handed to `renderOption` whole, as Mantine does (avatars, details…). */
  raw?: Record<string, unknown>;
}

interface PickerSection {
  group?: string;
  items: PickerOption[];
}

export interface OptionPickerModalProps {
  opened: boolean;
  onClose: () => void;
  /** Mantine Select-style data. */
  data: unknown;
  value?: string | null;
  onSelect?: (value: string | null) => void;
  title?: React.ReactNode;
  searchPlaceholder?: string;
  nothingFoundMessage?: React.ReactNode;
  clearable?: boolean;
  clearLabel?: string;
  /** Multi-select: rows toggle and the modal stays open until Done. */
  multiple?: boolean;
  /** Selected values in `multiple` mode. */
  values?: string[];
  /** Called with the tapped value in `multiple` mode. */
  onToggle?: (value: string) => void;
  /** Controlled search — provide with `onSearchChange` for server-side lookups. */
  searchValue?: string;
  /** When set, the caller owns filtering (the modal shows `data` as given). */
  onSearchChange?: (value: string) => void;
  /** Spinner in the search field (server-side lookup in flight). */
  loading?: boolean;
  /** Pinned "create" row under the search; receives the current search text. */
  createLabel?: string;
  onCreate?: (search: string) => void;
  /** Label of the Done button in `multiple` mode. */
  doneLabel?: string;
  /** Rendered under the search box (e.g. a hint) — above the options. */
  header?: React.ReactNode;
  /** Show the search box (default true). A short list of fixed choices reads better without one. */
  searchable?: boolean;
  /**
   * Draw each option's content — Mantine Select's `renderOption`, so a font picker shows each font in itself. It gets
   * the caller's whole data item (as Mantine passes it), and the row then draws no icon of its own (the caller's does).
   */
  renderOption?: (item: { option: { value: string; label: string; disabled?: boolean } & Record<string, unknown>; checked?: boolean }) => React.ReactNode;
  /** Single mode: tapping the chosen row empties the select (Mantine's `allowDeselect`; a desktop does the same). */
  allowDeselect?: boolean;
  /**
   * The picker's surface style. It portals to document.body, outside a themed page, so the public event site passes its
   * theme (`usePortalSurfaceStyle`) — as `AddressPickerModal` does.
   */
  contentStyle?: React.CSSProperties;
}

// ============================================
// The surface a themed page gives its pickers
// ============================================

const PickerSurfaceContext = createContext<React.CSSProperties | undefined>(undefined);

/**
 * The surface every full-screen picker under it is drawn on. The picker portals to document.body, OUTSIDE a themed
 * page, so the public event site provides its theme here ONCE (`EventThemeScope`) rather than at every select — a
 * select that forgot it would paint the base palette over a dark event theme. A `contentStyle` prop still wins.
 */
export const PickerSurfaceProvider = PickerSurfaceContext.Provider;

/** The surface a themed page provides (undefined in the dashboard) — the phone ActionSheet draws on it too. */
export const usePickerSurface = (): React.CSSProperties | undefined => useContext(PickerSurfaceContext);

// ============================================
// Normalize Mantine `data` → sections
// ============================================

const toOption = (item: unknown): PickerOption | null => {
  if (item == null) {
    return null;
  }

  if (typeof item === "string" || typeof item === "number") {
    return { value: String(item), label: String(item) };
  }

  const obj = item as { value?: unknown; label?: unknown; disabled?: boolean; icon?: string | null; color?: string | null };
  if (obj.value == null) {
    return null;
  }

  return {
    value: String(obj.value),
    label: String(obj.label ?? obj.value),
    disabled: obj.disabled,
    icon: obj.icon ?? null,
    color: obj.color ?? null,
    raw: obj as Record<string, unknown>,
  };
};

const normalize = (data: unknown): PickerSection[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  const flat: PickerOption[] = [];
  const sections: PickerSection[] = [];

  for (const entry of data) {
    if (entry && typeof entry === "object" && "group" in (entry as object) && "items" in (entry as object)) {
      const g = entry as { group?: string; items?: unknown[] };
      const items = (g.items ?? []).map(toOption).filter((o): o is PickerOption => o !== null);
      if (items.length) {
        sections.push({ group: g.group, items });
      }
    } else {
      const o = toOption(entry);
      if (o) {
        flat.push(o);
      }
    }
  }

  if (flat.length) {
    sections.unshift({ items: flat });
  }

  return sections;
};

// ============================================
// Component
// ============================================

export const OptionPickerModal: React.FC<OptionPickerModalProps> = ({
  opened,
  onClose,
  data,
  value,
  onSelect,
  title,
  searchPlaceholder,
  nothingFoundMessage = "No matches",
  clearable = false,
  clearLabel = "Clear selection",
  multiple = false,
  values,
  onToggle,
  searchValue,
  onSearchChange,
  loading = false,
  createLabel,
  onCreate,
  doneLabel = "Done",
  header,
  searchable = true,
  renderOption,
  contentStyle,
  allowDeselect = false,
}) => {
  const [internalSearch, setInternalSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const inheritedSurface = useContext(PickerSurfaceContext);
  const surface = contentStyle ?? inheritedSurface;

  // The caller owns the search (and the filtering) when it passes onSearchChange.
  const isExternalSearch = !!onSearchChange;
  const search = isExternalSearch ? searchValue ?? "" : internalSearch;

  const setSearch = (next: string) => {
    if (isExternalSearch) {
      onSearchChange?.(next);
      return;
    }

    setInternalSearch(next);
  };

  useEffect(() => {
    if (!opened) {
      setInternalSearch("");
    }
  }, [opened]);

  const sections = useMemo(() => normalize(data), [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q || isExternalSearch) {
      return sections;
    }

    return sections
      .map((s) => ({ ...s, items: s.items.filter((o) => o.label.toLowerCase().includes(q)) }))
      .filter((s) => s.items.length > 0);
  }, [sections, search, isExternalSearch]);

  const hasResults = filtered.some((s) => s.items.length > 0);
  const selectedValues = useMemo(() => new Set(values ?? []), [values]);

  const handlePick = (v: string) => {
    if (multiple) {
      onToggle?.(v);
      return;
    }

    onSelect?.(allowDeselect && value === v ? null : v);
    onClose();
  };

  const isSelected = (v: string): boolean => (multiple ? selectedValues.has(v) : value === v);

  return (
    <StackedModal
      opened={opened}
      onClose={onClose}
      fullScreen
      title={title}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100dvh - 60px)",
          padding: 0,
          overflow: "hidden",
        },
        header: { padding: "12px 16px", ...(surface ? { background: surface.background, color: surface.color } : {}) },
        content: { display: "flex", flexDirection: "column", ...surface },
      }}
    >
      {/* Sticky search */}
      {(searchable || header) && (
        <Box px="md" py="sm" style={{ flexShrink: 0, borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
          {searchable && (
            <TextInput
              ref={searchRef}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder={searchPlaceholder ?? "Search"}
              leftSection={<AppIcon iconName="Search" size={18} />}
              rightSection={
                loading ? (
                  <Loader size="sm" />
                ) : search ? (
                  <CloseButton onMouseDown={(e) => e.preventDefault()} onClick={() => setSearch("")} aria-label="Clear" />
                ) : undefined
              }
              size="md"
              autoComplete="off"
            />
          )}

          {header}
        </Box>
      )}

      {/* Options */}
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="scroll">
        <Stack gap={0} pb="md">
          {/* Create-in-place — pinned so it's reachable without scrolling the list. */}
          {createLabel && onCreate && (
            <UnstyledButton
              onClick={() => onCreate(search.trim())}
              w="100%"
              px="md"
              py="sm"
              style={{ borderBottom: "1px solid var(--mantine-color-gray-1)" }}
            >
              <Group gap="sm" wrap="nowrap">
                <AppIcon iconName="Plus" size={18} color="var(--mantine-color-brand-6)" />
                <Text size="md" c="brand.7" fw={600}>
                  {createLabel}
                </Text>
              </Group>
            </UnstyledButton>
          )}

          {clearable && !multiple && !search && (
            <UnstyledButton
              onClick={() => {
                onSelect?.(null);
                onClose();
              }}
              w="100%"
              px="md"
              py="sm"
              style={{ borderBottom: "1px solid var(--mantine-color-gray-1)" }}
            >
              <Text size="md" c="dimmed">
                {clearLabel}
              </Text>
            </UnstyledButton>
          )}

          {!hasResults && (
            <Box px="md" py="xl">
              <Text c="dimmed" ta="center" size="md">
                {nothingFoundMessage}
              </Text>
            </Box>
          )}

          {filtered.map((section, si) => (
            <Box key={section.group ?? `s-${si}`}>
              {section.group && (
                <Group
                  gap="xs"
                  px="md"
                  py="xs"
                  // The page's own surface (opaque, so rows scroll under it) — a fixed grey turned light on a dark event theme.
                  style={{ position: "sticky", top: 0, zIndex: 1, background: "var(--mantine-color-body)", borderBottom: "1px solid var(--mantine-color-gray-2)" }}
                >
                  <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                    {section.group}
                  </Text>
                </Group>
              )}

              {section.items.map((o) => {
                const selected = isSelected(o.value);

                return (
                  <UnstyledButton
                    key={o.value}
                    onClick={() => !o.disabled && handlePick(o.value)}
                    disabled={o.disabled}
                    w="100%"
                    px="md"
                    py="sm"
                    style={{
                      // A translucent tint of the primary colour, so a selected row reads on a dark event theme too.
                      backgroundColor: selected ? "var(--mantine-primary-color-light)" : undefined,
                      borderBottom: "1px solid var(--mantine-color-gray-1)",
                      opacity: o.disabled ? 0.45 : 1,
                      cursor: o.disabled ? "default" : "pointer",
                    }}
                  >
                    <Group gap="sm" wrap="nowrap">
                      {o.icon && !renderOption && <AppIcon iconName={o.icon} size={18} color={o.color || undefined} />}
                      {renderOption ? (
                        <Box style={{ flex: 1, minWidth: 0 }}>{renderOption({ option: { ...o.raw, value: o.value, label: o.label, disabled: o.disabled }, checked: selected })}</Box>
                      ) : (
                        <Text size="md" fw={selected ? 600 : 400} style={{ flex: 1, minWidth: 0 }}>
                          {o.label}
                        </Text>
                      )}
                      {selected && (
                        <AppIcon iconName="Check" size={18} color="var(--mantine-color-brand-6)" />
                      )}
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Box>
          ))}
        </Stack>
      </ScrollArea>

      {/* Multi-select needs an explicit way out (rows no longer close the modal). */}
      {multiple && (
        <Box px="md" py="sm" style={{ flexShrink: 0, borderTop: "1px solid var(--mantine-color-gray-3)" }}>
          <Button fullWidth size="md" onClick={onClose}>
            {doneLabel}
          </Button>
        </Box>
      )}
    </StackedModal>
  );
};
